import { useState } from "react";
import { MARKETPLACE_FEES, calculateResale, type MarketplaceFee } from "../../utils/toolCalculators";
import { toolInputClass, toolLabelClass } from "./toolUi";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const num = (raw: string) => {
  const value = Number(raw.replace(/[$,\s%]/g, ""));
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

type Field = "buy" | "tax" | "sale" | "shipCharged" | "shipCost" | "other";

const FIELDS: { key: Field; label: string; placeholder: string }[] = [
  { key: "buy", label: "You paid", placeholder: "e.g. 12.03" },
  { key: "tax", label: "Sales tax %", placeholder: "e.g. 7" },
  { key: "sale", label: "Sells for", placeholder: "e.g. 59.99" },
  { key: "shipCharged", label: "Shipping charged to buyer", placeholder: "0" },
  { key: "shipCost", label: "Your shipping cost", placeholder: "e.g. 9.50" },
  { key: "other", label: "Other costs (FBA, packaging)", placeholder: "0" },
];

export default function ResaleProfitTool() {
  const [values, setValues] = useState<Record<Field, string>>({ buy: "", tax: "", sale: "", shipCharged: "", shipCost: "", other: "" });
  const [fee, setFee] = useState<MarketplaceFee>(MARKETPLACE_FEES[0]);
  const [pctRaw, setPctRaw] = useState(String(MARKETPLACE_FEES[0].percent));
  const [fixedRaw, setFixedRaw] = useState(MARKETPLACE_FEES[0].fixed.toFixed(2));

  const pick = (id: string) => {
    const preset = MARKETPLACE_FEES.find((f) => f.id === id) ?? MARKETPLACE_FEES[0];
    setFee(preset);
    setPctRaw(String(preset.percent));
    setFixedRaw(preset.fixed.toFixed(2));
  };

  const ready = num(values.sale) > 0;
  const result = calculateResale({
    buyPrice: num(values.buy),
    taxRate: num(values.tax),
    salePrice: num(values.sale),
    shippingCharged: num(values.shipCharged),
    shippingCost: num(values.shipCost),
    otherCosts: num(values.other),
    fee,
  });
  const positive = result.profit >= 0;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-3">
        {FIELDS.map((f) => (
          <label key={f.key}>
            <span className={toolLabelClass}>{f.label}</span>
            <input
              className={toolInputClass}
              inputMode="decimal"
              placeholder={f.placeholder}
              value={values[f.key]}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
          </label>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-3">
        <span className={toolLabelClass}>Marketplace</span>
        <div className="flex flex-wrap gap-2">
          {MARKETPLACE_FEES.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => pick(f.id)}
              className={`rounded-full border px-3 py-1 text-sm font-semibold ${fee.id === f.id ? "border-violet-700 bg-violet-700 text-white" : "border-slate-300 hover:border-violet-400"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3 text-sm">
          <label>
            <span className="block text-xs font-semibold text-slate-600 mb-1">Fee %</span>
            <input className={`${toolInputClass} w-24`} inputMode="decimal" value={pctRaw} onChange={(e) => { setPctRaw(e.target.value); setFee({ ...fee, percent: num(e.target.value) }); }} />
          </label>
          <label>
            <span className="block text-xs font-semibold text-slate-600 mb-1">Fixed fee $</span>
            <input className={`${toolInputClass} w-24`} inputMode="decimal" value={fixedRaw} onChange={(e) => { setFixedRaw(e.target.value); setFee({ ...fee, fixed: num(e.target.value), smallOrderFixed: undefined }); }} />
          </label>
          {fee.note && <p className="text-xs text-slate-500 basis-full">{fee.note}</p>}
        </div>
      </div>

      {ready && (
        <div className={`mt-5 rounded-xl border p-4 sm:p-5 ${positive ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} role="status">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Profit</p>
          <p className={`mt-1 text-3xl font-extrabold ${positive ? "text-emerald-800" : "text-red-700"}`}>{money(result.profit)}</p>
          <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm sm:grid-cols-4">
            <div><dt className="text-slate-600">ROI</dt><dd className="font-bold">{result.roi === null ? "—" : `${result.roi}%`}</dd></div>
            <div><dt className="text-slate-600">Margin</dt><dd className="font-bold">{result.margin === null ? "—" : `${result.margin}%`}</dd></div>
            <div><dt className="text-slate-600">{fee.label} fees</dt><dd className="font-bold">{money(result.fees)}</dd></div>
            <div><dt className="text-slate-600">Break-even price</dt><dd className="font-bold">{money(result.breakEven)}</dd></div>
          </dl>
          <p className="mt-3 text-xs text-slate-500">Your cost with tax: {money(result.cost)}. Fees are estimates; check the marketplace's current rates.</p>
        </div>
      )}
    </div>
  );
}
